'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Notification } from '@/lib/notifications/notification-types';
import useNotificationStore from '@/lib/hooks/zustand-stores/use-notifications-store';
import NotificationPreviewSingle from '@/components/notifications/notification-preview-single';
import { useLocalStorage } from '@/lib/hooks/use-local-storage';
import { useLogger } from '@/lib/hooks/useLogger';
import useWebSocketStore from '@/lib/hooks/zustand-stores/use-websocket-store';

// Constants for consistent positioning
const MAX_VISIBLE_NOTIFICATIONS = 3;

export const NotificationPreviewProvider: React.FC = () => {
  // Track active notification previews
  const [activeNotifications, setActiveNotifications] = useState<Notification[]>([]);
  const [showPreviews] = useLocalStorage('showNotificationPreviews', true);
  const logger = useLogger();
  
  // Enhanced duplicate tracking - track by both ID and content hash
  const [displayedNotifications, setDisplayedNotifications] = useState<Set<string>>(new Set());
  const unreadNotifications = useNotificationStore(state => state.unreadNotifications);
  
  // Add a reference to track recent notifications with timestamps
  const recentNotificationsRef = useRef<Map<string, {timestamp: number, contentHash: string}>>(new Map());
  
  // Utility function to create a content hash from notification properties
  // This will help detect functionally identical notifications
  const createContentHash = (notification: Notification): string => {
    return `${notification.type}:${notification.chatId}:${notification.message}:${notification.userName}`;
  };
  
  // Calculate position indices based on notification order
  const getPositionIndices = () => {
    const indices: Record<string, number> = {};
    
    // Always ensure notifications are positioned from top to bottom (0, 1, 2)
    activeNotifications.slice(0, MAX_VISIBLE_NOTIFICATIONS).forEach((notification, index) => {
      indices[notification._id] = index;
    });
    
    return indices;
  };

  // When notifications change, recalculate positions and ensure proper stacking
  useEffect(() => {
    // This is an empty effect dependency on activeNotifications
    // It will trigger getPositionIndices() whenever the notification stack changes
  }, [activeNotifications]);
  
  // Helper function to ensure no duplicate IDs in the notifications array
  const addNotificationSafely = (notification: Notification, currentList: Notification[]) => {
    // First check if this ID already exists in the list
    const exists = currentList.some(item => item._id === notification._id);
    
    // If it exists, don't add a duplicate
    if (exists) {
      logger.debug(`Preventing duplicate notification ID in active list: ${notification._id}`);
      return currentList; // Return unchanged list
    }
    
    // Add to front of list and maintain max size
    const updated = [notification, ...currentList].slice(0, MAX_VISIBLE_NOTIFICATIONS);
    return updated;
  };
  
  // Clean up old notifications from the recent tracking map
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const recentNotifications = recentNotificationsRef.current;
      
      // Remove entries older than 10 seconds
      for (const [id, data] of recentNotifications.entries()) {
        if (now - data.timestamp > 10000) {
          recentNotifications.delete(id);
        }
      }
    }, 30000); // Run cleanup every 30 seconds
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // Listen for WebSocket notifications 
  useEffect(() => {
    if (!showPreviews) return;
    
    // Get the current message count to track new ones
    let prevMessageCount = useWebSocketStore.getState().messages.length;
    
    // Subscribe to WebSocket messages
    const unsubscribe = useWebSocketStore.subscribe((state) => {
      const messages = state.messages;
      
      // Skip if no new messages
      if (messages.length <= prevMessageCount) {
        prevMessageCount = messages.length;
        return;
      }
      
      // Check new messages for notifications
      const newMessages = messages.slice(prevMessageCount);
      prevMessageCount = messages.length;
      
      const notificationMessages = newMessages.filter(msg => 
        msg.type === 'notification' || 
        msg.type === 'newNotification'
      );
      
      // Process new notification messages
      if (notificationMessages.length > 0) {
        notificationMessages.forEach(msg => {
          const notification = msg.data;
          const contentHash = createContentHash(notification);
          const now = Date.now();
          
          // Check for exact duplicate by ID
          if (displayedNotifications.has(notification._id)) {
            logger.debug(`Skipping duplicate notification by ID: ${notification._id}`);
            return;
          }
          
          // Check for functional duplicate by content
          const recentNotifications = recentNotificationsRef.current;
          let isDuplicate = false;
          
          for (const [id, data] of recentNotifications.entries()) {
            // Check if we have a very recent notification with the same content hash
            // AND from the same time window (within 5 seconds)
            if (data.contentHash === contentHash && now - data.timestamp < 5000) {
              logger.debug(`Skipping functional duplicate notification: ${notification._id} (similar to ${id})`);
              isDuplicate = true;
              break;
            }
          }
          
          if (isDuplicate) return;
          
          // Add to recent notifications tracking
          recentNotificationsRef.current.set(notification._id, {
            timestamp: now,
            contentHash
          });
          
          // Add notification to active list with duplicate prevention
          setActiveNotifications(prev => addNotificationSafely(notification, prev));
          
          // Add to displayed set
          setDisplayedNotifications(prev => {
            const next = new Set(prev);
            next.add(notification._id);
            return next;
          });
        });
      }
    });
    
    return () => unsubscribe();
  }, [showPreviews, displayedNotifications, logger]);
  
  // Listen for custom notification events (tests or direct triggers)
  useEffect(() => {
    const handleCustomNotification = (event: CustomEvent) => {
      if (!showPreviews) return;
      
      const { notification, isTest } = event.detail || {};
      logger.info('Received custom notification event:', notification);
      
      // Skip if we've already shown this notification (unless it's a test)
      if (!isTest && displayedNotifications.has(notification._id)) {
        logger.debug(`Skipping already displayed notification: ${notification._id}`);
        return;
      }
      
      // Add to active notifications with duplicate prevention
      setActiveNotifications(prev => addNotificationSafely(notification, prev));
      
      if (!isTest) {
        // Add to displayed set
        setDisplayedNotifications(prev => {
          const next = new Set(prev);
          next.add(notification._id);
          return next;
        });
      }
    };
    
    window.addEventListener('show-notification-preview', handleCustomNotification as EventListener);
    
    return () => {
      window.removeEventListener('show-notification-preview', handleCustomNotification as EventListener);
    };
  }, [showPreviews, displayedNotifications, logger]);
  
  // Handle store changes directly too (fallback mechanism)
  useEffect(() => {
    if (!showPreviews) return;
    
    const lastKnownCount = unreadNotifications.length;
    
    const unsubscribe = useNotificationStore.subscribe((state) => {
      // Only proceed if there are new notifications
      if (state.unreadNotifications.length > lastKnownCount && state.unreadNotifications.length > 0) {
        const newestNotification = state.unreadNotifications[0];
        
        // Skip if already displayed
        if (displayedNotifications.has(newestNotification._id)) {
          logger.debug(`Skipping already displayed notification from store: ${newestNotification._id}`);
          return;
        }
        
        // Add to active notifications with duplicate prevention
        setActiveNotifications(prev => addNotificationSafely(newestNotification, prev));
        
        // Add to displayed set
        setDisplayedNotifications(prev => {
          const next = new Set(prev);
          next.add(newestNotification._id);
          return next;
        });
      }
    });
    
    return () => unsubscribe();
  }, [unreadNotifications.length, displayedNotifications, showPreviews, logger]);
  
  // Clear displayed notifications after 24 hours
  useEffect(() => {
    const intervalId = setInterval(() => {
      setDisplayedNotifications(new Set());
    }, 24 * 60 * 60 * 1000); // 24 hours
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Handle closing a notification
  const handleCloseNotification = (notificationId: string) => {
    setActiveNotifications(prev => {
      // Remove the closed notification
      const filtered = prev.filter(notification => notification._id !== notificationId);
      
      // Return the updated list, ensuring indices stay consistent
      return filtered;
    });
  };
  
  if (!showPreviews || activeNotifications.length === 0) return null;
  
  // Calculate position indices for proper stacking
  const positionIndices = getPositionIndices();

  // Ensure we don't have any duplicate notification IDs before rendering
  // This provides a safeguard in case any slipped through our other checks
  const uniqueNotifications = Array.from(
    new Map(activeNotifications.map(item => [item._id, item])).values()
  ).slice(0, MAX_VISIBLE_NOTIFICATIONS);
  
  return (
    <div className="notification-previews-container">
      {uniqueNotifications.map((notification, index) => (
        <NotificationPreviewSingle
          key={`${notification._id}-${index}`}
          notification={notification}
          onClose={() => handleCloseNotification(notification._id)}
          bypassWebSocket={true}
          index={positionIndices[notification._id] || index}
        />
      ))}
    </div>
  );
}; 